<template>
  <div class="portal-home">
    <section class="hero">
      <!-- 桌面端：整图轮播 + 透明热点 -->
      <el-carousel
        class="hero-carousel hero-carousel--desktop"
        height="100%"
        :interval="5000"
        arrow="hover"
        indicator-position="inside"
      >
        <el-carousel-item v-for="(slide, idx) in heroSlideList" :key="`d-${idx}`">
          <img
            :src="slide.image"
            :alt="`${slide.title}${slide.highlight || ''}，${slide.description || ''}`"
          />
        </el-carousel-item>
      </el-carousel>
      <button class="hero-hotspot hero-trial" aria-label="免费试用" @click="handleTrial"></button>
      <button
        class="hero-hotspot hero-contact"
        aria-label="联系销售"
        @click="handleContact"
      ></button>

      <!-- 手机端：专用整图轮播 -->
      <el-carousel
        class="hero-carousel hero-carousel--mobile"
        height="100%"
        :interval="5000"
        arrow="hover"
        indicator-position="outside"
      >
        <el-carousel-item v-for="(src, idx) in heroMobileSlides" :key="`m-${idx}`">
          <img :src="src" :alt="heroMobileAlts[idx]" />
        </el-carousel-item>
      </el-carousel>
      <button
        class="hero-hotspot hero-hotspot--mobile hero-trial-mobile"
        aria-label="免费试用"
        @click="handleTrial"
      ></button>
      <button
        class="hero-hotspot hero-hotspot--mobile hero-contact-mobile"
        aria-label="联系销售"
        @click="handleContact"
      ></button>
    </section>

    <section class="page-section control-center-section">
      <div class="shell control-shell">
        <SectionTitle
          title="企业AI控制中心"
          sub="硬件算力、模型接入、企业知识、Skills能力与Agent编排统一建设，控制权始终留在企业内部。"
        />
        <div class="control-layout">
          <aside class="control-pitch">
            <div class="control-pitch-brand">
              <div class="control-pitch-mark" aria-hidden="true">
                <Icon icon="lucide:layers" :size="28" />
              </div>
              <div>
                <p class="control-pitch-eyebrow">紫金山企业 AI 控制中心</p>
                <h3>一体化运行<br />安全可控</h3>
              </div>
            </div>
            <ul class="control-pitch-list">
              <li v-for="item in controlPitchItems" :key="item.no">
                <span class="control-pitch-no">{{ item.no }}</span>
                <span class="control-pitch-icon" :style="{ color: item.color, background: item.bg }">
                  <Icon :icon="item.icon" :size="18" />
                </span>
                <div>
                  <strong>{{ item.title }}</strong>
                  <p>{{ item.desc }}</p>
                </div>
              </li>
            </ul>
            <div class="control-pitch-actions">
              <button type="button" class="control-btn primary" @click="handleTrial">免费体验</button>
              <button type="button" class="control-btn ghost" @click="handleContact">预约演示</button>
              <button type="button" class="control-link" @click="navigateToControlCenter">
                了解更多
                <Icon icon="lucide:chevron-right" :size="14" />
              </button>
            </div>
          </aside>

          <div class="control-arch">
            <header class="control-arch-head">
              <span class="control-arch-eyebrow">整体能力架构</span>
              <h3>从基础设施到岗位应用，一套系统完整交付</h3>
            </header>
            <div class="control-arch-grid">
              <article
                v-for="item in controlArchCards"
                :key="item.title"
                class="control-arch-card"
                :style="{ '--accent': item.color, '--soft': item.bg }"
              >
                <span class="control-arch-bar" aria-hidden="true"></span>
                <span class="control-arch-icon">
                  <Icon :icon="item.icon" :size="22" />
                </span>
                <h4>{{ item.title }}</h4>
                <p>{{ item.desc }}</p>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="page-section core-section">
      <div class="shell core-shell">
        <SectionTitle
          title="核心产品矩阵"
          sub="一键贯通工具、应用、业务与数据，打破孤岛，释放企业数智生产力"
        />
        <div class="product-grid">
          <article
            v-for="item in productCards"
            :key="item.id"
            class="product-card"
            :style="{ '--accent': item.color, '--soft': item.bg }"
            role="link"
            tabindex="0"
            :aria-label="`${item.title}，了解更多`"
            @click="navigateToProduct(item)"
            @keydown.enter.prevent="navigateToProduct(item)"
          >
            <img class="product-card-deco" :src="item.image" alt="" loading="lazy" />
            <div class="product-icon-stack" aria-hidden="true">
              <div class="product-icon-tile">
                <Icon :icon="item.icon" :size="30" />
              </div>
              <div class="product-icon-pedestal">
                <span class="pedestal-top"></span>
                <span class="pedestal-base"></span>
              </div>
            </div>
            <div class="product-copy">
              <h3>{{ item.title }}</h3>
              <p>
                <template v-for="(line, i) in item.desc" :key="i">
                  {{ line }}<br v-if="i < item.desc.length - 1" />
                </template>
              </p>
              <button type="button" tabindex="-1">
                了解更多
                <Icon icon="lucide:chevron-right" :size="14" />
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="page-section hot-section">
      <div class="shell hot-shell">
        <SectionTitle title="热门产品推荐" sub="最受企业欢迎的数字化产品，即买即用，3分钟上线" />
        <div class="hot-grid">
          <article
            v-for="item in hotProducts"
            :key="item.id"
            class="hot-card"
            :style="{ '--accent': item.color, '--soft': item.bg }"
          >
            <div class="hot-icon-wrap" aria-hidden="true">
              <img class="hot-icon" :src="item.image" alt="" loading="lazy" />
            </div>
            <div class="hot-body">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div class="hot-actions">
                <button type="button" class="hot-btn" @click.stop="handleTrial">在线咨询</button>
                <button type="button" class="hot-btn" @click.stop="handleContact">联系销售</button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="page-section solutions-section">
      <div class="shell solutions-shell">
        <SectionTitle
          title="行业解决方案"
          sub="深耕行业场景，定制智能方案，精准破解痛点，助力企业领跑数智时代"
        />
        <div class="solution-grid">
          <article
            v-for="item in solutionCards"
            :key="item.slug"
            class="solution-card"
            role="link"
            tabindex="0"
            :aria-label="`${item.title}，查看完整方案`"
            @click="navigateToSolution(item)"
            @keydown.enter.prevent="navigateToSolution(item)"
          >
            <img class="solution-card-bg" :src="item.image" :alt="item.title" />
            <div class="solution-card-mask" aria-hidden="true"></div>
            <div class="solution-copy">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div v-if="item.tags.length" class="solution-tags">
                <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
              </div>
              <span class="solution-cta">
                查看完整方案
                <Icon icon="lucide:arrow-right" :size="14" />
              </span>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="page-section advantage-section">
      <div class="shell advantage-shell">
        <SectionTitle
          title="为什么选择紫金山AI"
          sub="六大核心优势，为企业数字化转型保驾护航，不止于技术交付，更在于业务增长，让AI投资看得见回报"
        />
        <div class="adv-grid">
          <article
            v-for="item in advantages"
            :key="item.title"
            :style="{ '--accent': item.color, '--soft': item.bg }"
            ><img class="adv-icon" :src="item.image" :alt="item.title" loading="lazy" />
            <section>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p
              ><button @click="handleTrial">在线咨询</button>
            </section>
          </article>
        </div>
      </div>
    </section>

    <section class="stats-band">
      <div class="shell stats-grid">
        <div v-for="item in stats" :key="item.label"
          ><strong
            >{{ item.value }}<sup>{{ item.suffix }}</sup></strong
          ><span>{{ item.label }}</span></div
        >
      </div>
    </section>

    <section class="page-section customer-section">
      <div class="shell customer-shell">
        <SectionTitle
          title="1000+头部企业都在选择我们"
          sub="我们服务涵盖了综合体、新零售、物业、美容美妆、家装建材、服饰家纺、数码家电、商超百货、制造业、金融等各行各业"
        />
        <div class="logo-wall">
          <div v-for="logo in customerLogos" :key="logo.name" class="logo-item">
            <img :src="logo.image" :alt="logo.name" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="shell cta-inner">
        <div class="cta-eyebrow"> <Icon icon="lucide:rocket" />企业数字化转型专家 </div>
        <h2>立即开始您的数字化转型</h2>
        <p>10+款产品免费试用7-14天</p>
        <div
          ><button class="cta-light" @click="handleTrial">
            <Icon icon="lucide:rocket" />免费试用 </button
          ><button class="cta-line" @click="handleContact">
            <Icon icon="lucide:phone" :size="16" />联系销售
          </button></div
        >
        <aside
          ><span> <Icon icon="lucide:message-circle" />客服热线 {{ serviceHotline }} </span
          ><span> <Icon icon="lucide:clock-3" />服务时间 9:00-18:00 </span></aside
        >
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { openCustomerService } from './openCustomerService'
import { defineComponent, h, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { getPortalBanners, getPortalSiteConfig } from '@/api/portal'
import banner01 from '@/assets/zjscc-banner/banner01.png'
import banner02 from '@/assets/zjscc-banner/banner02.png'
import banner03 from '@/assets/zjscc-banner/banner03.png'
import banner04 from '@/assets/zjscc-banner/banner04.png'
import banner05 from '@/assets/zjscc-banner/banner05.png'
import banner06 from '@/assets/zjscc-banner/banner06.png'
import banner07 from '@/assets/zjscc-banner/banner07.png'
import mobileBanner01 from '@/assets/zjscc-banner/moblie/1.1.png'
import mobileBanner02 from '@/assets/zjscc-banner/moblie/1.2.png'
import mobileBanner03 from '@/assets/zjscc-banner/moblie/1.3.png'
import mobileBanner04 from '@/assets/zjscc-banner/moblie/1.4.png'
import mobileBanner05 from '@/assets/zjscc-banner/moblie/1.5.png'
import mobileBanner06 from '@/assets/zjscc-banner/moblie/1.6.png'
import mobileBanner07 from '@/assets/zjscc-banner/moblie/1.7.png'
import productAiTools from '@/assets/portal/home/hot-product-ai-tools.png'
import productGeo from '@/assets/portal/home/hot-product-geo.png'
import productAiCommerce from '@/assets/portal/home/hot-product-ai-commerce.png'
import productAiAcademy from '@/assets/portal/home/hot-product-ai-academy.png'
import productAiBusinessSocial from '@/assets/portal/home/hot-product-ai-business-social.png'
import productAiTravel from '@/assets/portal/home/hot-product-ai-travel.png'
import productAiSocial from '@/assets/portal/home/hot-product-ai-social.png'
import productAiLive from '@/assets/portal/home/hot-product-ai-live.png'
import advantageSecurity from '@/assets/portal/home/advantage-security.png'
import advantageFastLaunch from '@/assets/portal/home/advantage-fast-launch.png'
import advantageSupport from '@/assets/portal/home/advantage-support.png'
import advantageModular from '@/assets/portal/home/advantage-modular.png'
import advantageUpgrade from '@/assets/portal/home/advantage-upgrade.png'
import advantagePricing from '@/assets/portal/home/advantage-pricing.png'
import coreAiTools from '@/assets/portal/home/core-ai-tools.png'
import coreAiEnterpriseApp from '@/assets/portal/home/core-ai-enterprise-app.png'
import coreAiAppPlatform from '@/assets/portal/home/core-ai-app-platform.png'
import coreAiBusinessPlatform from '@/assets/portal/home/core-ai-business-platform.png'
import coreAiDataPlatform from '@/assets/portal/home/core-ai-data-platform.png'
import solutionRetail from '@/assets/portal/home/solution-retail.png'
import solutionManufacturing from '@/assets/portal/home/solution-manufacturing.png'
import solutionEducation from '@/assets/portal/home/solution-education.png'
import solutionHealthcare from '@/assets/portal/home/solution-healthcare.png'
import solutionFinance from '@/assets/portal/home/solution-finance.png'
import solutionHospitality from '@/assets/portal/home/solution-hospitality.png'
import { getPortalSolutionCatalogByCategory } from './data/portalCatalog'

const customerLogoModules = import.meta.glob('@/assets/portal/home/customer-*.png', {
  eager: true,
  import: 'default'
}) as Record<string, string>

defineOptions({ name: 'PortalHome' })
const router = useRouter()

type HeroSlide = {
  image: string
  tag?: string
  title: string
  highlight?: string
  description?: string
  primaryText?: string
  secondaryText?: string
}

const defaultHeroSlides: HeroSlide[] = [
  {
    image: banner06,
    tag: 'AI驱动企业全场景智能升级',
    title: 'AI企业服务',
    highlight: '重塑效率 激活增长',
    description: '智能办公 · 客户服务 · 流程自动化 · 数据决策',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  },
  {
    image: banner01,
    tag: 'AI驱动企业增长',
    title: '让数字化',
    highlight: '触手可及',
    description:
      '100+款产品即买即用，覆盖建站营销、电商交易、AI智能、业务中台、数据中台全场景。插件化配置，3分钟上线。',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  },
  {
    image: banner02,
    tag: 'AI驱动全球生意增长',
    title: 'AI跨境电商',
    highlight: '让全球生意更简单',
    description: '智能选品 · 多语运营 · 全球营销 · 高效履约',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  },
  {
    image: banner03,
    tag: 'AI赋能业务升级',
    title: 'AI智能应用',
    highlight: '驱动企业增长',
    description: '多场景 AI 应用即买即用，快速落地业务智能化',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  },
  {
    image: banner04,
    tag: '全链路数字化',
    title: '智慧商业',
    highlight: '一站式解决方案',
    description: '商城、会员、营销、数据中台统一建设',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  },
  {
    image: banner05,
    tag: '企业级 AI 平台',
    title: 'ZJSClaw',
    highlight: '企业 AI 控制中心',
    description: '模型接入、知识库、Skills 与 Agent 编排统一交付',
    primaryText: '免费体验',
    secondaryText: '预约演示'
  },
  {
    image: banner07,
    tag: '紫金山 AI',
    title: '让 AI',
    highlight: '真正落地业务',
    description: '从工具到应用，从平台到行业方案，全栈可交付',
    primaryText: '立即体验',
    secondaryText: '了解方案'
  }
]

const heroSlideList = ref<HeroSlide[]>([...defaultHeroSlides])
const heroMobileSlides = [
  mobileBanner06,
  mobileBanner01,
  mobileBanner02,
  mobileBanner03,
  mobileBanner04,
  mobileBanner05,
  mobileBanner07
]
const heroMobileAlts = defaultHeroSlides.map(
  (slide) => `${slide.title}${slide.highlight || ''}，${slide.description || ''}`
)
const serviceHotline = ref('400-646-5288')

const splitHeroTitle = (raw?: string) => {
  const text = (raw || '').trim()
  if (!text) return { title: '', highlight: '' }
  const parts = text.split(/[|｜\n]/).map((s) => s.trim()).filter(Boolean)
  if (parts.length >= 2) return { title: parts[0], highlight: parts.slice(1).join('') }
  const comma = text.match(/^(.{2,12}?)([，,].+)$/)
  if (comma) return { title: comma[1], highlight: comma[2].replace(/^[，,]/, '') }
  return { title: text, highlight: '' }
}

const mapBannerToSlide = (banner: Record<string, any>, fallback: HeroSlide): HeroSlide => {
  const { title, highlight } = splitHeroTitle(banner.title)
  const description =
    (banner.linkUrl && !/^https?:\/\//i.test(banner.linkUrl) ? banner.linkUrl : '') ||
    banner.content ||
    fallback.description
  return {
    image: banner.imgUrl || banner.thumbUrl || fallback.image,
    tag: banner.content && banner.content !== description ? banner.content : fallback.tag,
    title: title || fallback.title,
    highlight: highlight || fallback.highlight,
    description: description || fallback.description,
    primaryText: fallback.primaryText,
    secondaryText: fallback.secondaryText
  }
}

const SectionTitle = defineComponent({
  props: { title: String, sub: String },
  setup: (props) => () =>
    h('header', { class: 'section-heading' }, [h('h2', props.title), h('p', props.sub)])
})

type HomeSolutionCard = {
  title: string
  image: string
  desc: string
  icon: string
  watermark: string
  color: string
  bg: string
  tags: string[]
  slug: string
  path: string
}

const solutionCoverBySlug: Record<string, string> = {
  retail: solutionRetail,
  manufacturing: solutionManufacturing,
  education: solutionEducation,
  healthcare: solutionHealthcare,
  finance: solutionFinance,
  catering: solutionHospitality,
  hospitality: solutionHospitality,
  realestate: solutionManufacturing,
  government: solutionEducation,
  media: solutionHealthcare,
  agriculture: solutionRetail,
  energy: solutionFinance,
  'portal-test-industry': solutionRetail
}

const defaultSolutionCards: HomeSolutionCard[] = [
  {
    title: '零售电商',
    image: solutionRetail,
    desc: '全渠道商城、会员运营、营销工具与数据中台，助力线上线下融合增长',
    icon: 'lucide:shopping-bag',
    watermark: 'lucide:shopping-cart',
    color: '#f43f75',
    bg: '#fff0f5',
    tags: ['商城系统', '会员系统'],
    slug: 'retail',
    path: '/portal/solutions/retail'
  },
  {
    title: '智能制造',
    image: solutionManufacturing,
    desc: 'MES+ERP+SRM+数据中台，打造透明车间与智能供应链',
    icon: 'lucide:factory',
    watermark: 'lucide:bot',
    color: '#8b5cf6',
    bg: '#f5f0ff',
    tags: ['MES', 'ERP'],
    slug: 'manufacturing',
    path: '/portal/solutions/manufacturing'
  },
  {
    title: '教育培训',
    image: solutionEducation,
    desc: '课程管理、学员运营、活动报名与CRM，教培机构全链路数字化',
    icon: 'lucide:book-open',
    watermark: 'lucide:graduation-cap',
    color: '#1478ff',
    bg: '#edf5ff',
    tags: ['培训课程', 'CRM'],
    slug: 'education',
    path: '/portal/solutions/education'
  },
  {
    title: '医疗健康',
    image: solutionHealthcare,
    desc: '在线问诊、患者管理、预约挂号与电子病历，智慧医疗全场景覆盖',
    icon: 'lucide:briefcase-medical',
    watermark: 'lucide:heart-pulse',
    color: '#14b86c',
    bg: '#edfff6',
    tags: ['预约系统', 'IM通讯'],
    slug: 'healthcare',
    path: '/portal/solutions/healthcare'
  },
  {
    title: '金融保险',
    image: solutionFinance,
    desc: '智能风控、客户管理、产品配置与数据分析，金融保险数字化升级',
    icon: 'lucide:landmark',
    watermark: 'lucide:shield-check',
    color: '#f2a000',
    bg: '#fff8e8',
    tags: ['CRM', '数据中台'],
    slug: 'finance',
    path: '/portal/solutions/finance'
  },
  {
    title: '餐饮旅游',
    image: solutionHospitality,
    desc: '自营外卖、会员营销、门店管理与供应链，降低平台佣金依赖',
    icon: 'lucide:utensils',
    watermark: 'lucide:luggage',
    color: '#ff7b19',
    bg: '#fff3e9',
    tags: ['商城系统', '会员系统'],
    slug: 'hospitality',
    path: '/portal/solutions/catering'
  }
]

const solutionCards = ref<HomeSolutionCard[]>([...defaultSolutionCards])

onMounted(async () => {
  const [bannerResult, configResult, solutionResult] = await Promise.allSettled([
    getPortalBanners('portal_home'),
    getPortalSiteConfig(),
    getPortalSolutionCatalogByCategory('industry-suite')
  ])
  if (bannerResult.status === 'fulfilled' && Array.isArray(bannerResult.value) && bannerResult.value.length) {
    heroSlideList.value = bannerResult.value.map((banner, idx) =>
      mapBannerToSlide(banner, defaultHeroSlides[idx] || defaultHeroSlides[0])
    )
  }
  if (configResult.status === 'fulfilled' && configResult.value)
    serviceHotline.value =
      configResult.value.tel400 || configResult.value.mobile || serviceHotline.value

  if (solutionResult.status === 'fulfilled' && solutionResult.value.length) {
    // 固定取 industry-suite 列表前 6 条（按接口返回顺序，不再筛推荐）
    const source = solutionResult.value.slice(0, 6)
    solutionCards.value = source.map((item) => {
      const color = item.color || '#0066FF'
      return {
        title: item.shortName || item.name.replace(/解决方案$/, ''),
        image: item.coverImage || solutionCoverBySlug[item.id] || solutionRetail,
        desc: item.desc || item.effect || item.descLong,
        icon: item.icon || 'lucide:layers',
        watermark: item.icon || 'lucide:layers',
        color,
        bg: `${color}14`,
        tags: (item.products || []).slice(0, 2),
        slug: item.id,
        path: item.path || `/portal/solutions/${item.id}`
      }
    })
  }
})


const handleTrial = () => openCustomerService()
const handleContact = () => openCustomerService()
const navigateToProduct = (item: { path: string }) => router.push(item.path)
const navigateToControlCenter = () => router.push('/portal/zjsclaw')
const navigateToSolution = (item: { path?: string; slug: string }) => {
  const path = item.path || `/portal/solutions/${item.slug}`
  if (!path) return
  window.open(path, '_blank', 'noopener,noreferrer')
}

const controlPitchItems = [
  {
    no: '01',
    title: 'AI 统一入口',
    desc: '多模型聚合与智能路由',
    icon: 'lucide:layers',
    color: '#0667f9',
    bg: '#eaf2ff'
  },
  {
    no: '02',
    title: 'Skills 仓库',
    desc: '复用企业 SOP 与岗位 Know-how',
    icon: 'lucide:box',
    color: '#0667f9',
    bg: '#eaf2ff'
  },
  {
    no: '03',
    title: 'Agent 编排',
    desc: '多 Agent 协同完成复杂任务',
    icon: 'lucide:share-2',
    color: '#0667f9',
    bg: '#eaf2ff'
  },
  {
    no: '04',
    title: '企业 AI 主权',
    desc: '模型、数据、知识和流程可控',
    icon: 'lucide:shield-check',
    color: '#0667f9',
    bg: '#eaf2ff'
  }
]

const controlArchCards = [
  {
    title: 'AI 应用层',
    desc: '合同审核 / 发票处理 / 知识问答 / 运营分析',
    icon: 'lucide:layout-grid',
    color: '#0667f9',
    bg: '#edf5ff'
  },
  {
    title: '开放集成层',
    desc: '企微 / 钉钉 / CRM / MES / API / Webhook',
    icon: 'lucide:cloud',
    color: '#0667f9',
    bg: '#edf5ff'
  },
  {
    title: 'AI 工程层',
    desc: '任务理解 / SOP 编排 / Skills 调用 / 全程追踪',
    icon: 'lucide:clipboard-list',
    color: '#0667f9',
    bg: '#edf5ff'
  },
  {
    title: '企业知识库',
    desc: '文件解析 / 知识检索 / 结构与权限维护',
    icon: 'lucide:file-text',
    color: '#ff8a2b',
    bg: '#fff6eb'
  },
  {
    title: '模型服务层',
    desc: '千问 / 智谱 / DeepSeek / 私有模型 / 混合路由',
    icon: 'lucide:box',
    color: '#8154ef',
    bg: '#f4efff'
  },
  {
    title: '基础设施层',
    desc: 'GPU / CPU / 存储 / 网络 / 安全审计',
    icon: 'lucide:server',
    color: '#334155',
    bg: '#f1f5f9'
  }
]

const productCards = [
  {
    id: 'ai-tools',
    title: 'AI工具',
    desc: ['智简未来，效能倍增，', '你的全天候副手'],
    icon: 'lucide:wrench',
    image: coreAiTools,
    color: '#0667f9',
    bg: '#edf5ff',
    path: '/portal/products'
  },
  {
    id: 'ai-enterprise',
    title: 'AI企业应用',
    desc: ['行业场景深度适配，', '打造专属智能应用'],
    icon: 'lucide:building-2',
    image: coreAiEnterpriseApp,
    color: '#2f7cf6',
    bg: '#eef5ff',
    path: '/portal/products'
  },
  {
    id: 'ai-app',
    title: 'AI应用中台',
    desc: ['敏捷连接智能，', '业务创新随心所欲'],
    icon: 'lucide:layers',
    image: coreAiAppPlatform,
    color: '#08bd72',
    bg: '#edfff7',
    path: '/portal/products'
  },
  {
    id: 'ai-biz',
    title: 'AI业务中台',
    desc: ['数智驱动决策，', '全局协同，赢在当下'],
    icon: 'lucide:briefcase',
    image: coreAiBusinessPlatform,
    color: '#8154ef',
    bg: '#f4efff',
    path: '/portal/biz'
  },
  {
    id: 'ai-data',
    title: 'AI数据中台',
    desc: ['融通数据孤岛，', '炼就洞察，赋能万变'],
    icon: 'lucide:database',
    image: coreAiDataPlatform,
    color: '#ff9100',
    bg: '#fff6e9',
    path: '/portal/data'
  }
]
const stats = [
  { value: '10,000', suffix: '+', label: '企业用户' },
  { value: '1,00', suffix: '+', label: '款产品' },
  { value: '99.9', suffix: '%', label: 'SLA可用性' },
  { value: '100', suffix: '%', label: '服务覆盖' }
]
const customerNames = [
  '中国联通',
  '富士康',
  '华测检测',
  '谱尼测试',
  '顺丰速运',
  '深圳广电',
  '宜和购物',
  '东方电气',
  '名雕装饰',
  '深圳水务',
  '紫罗兰',
  '佳驰科技',
  '蓝凌',
  '缅甸佤邦电网',
  '爱婴岛',
  '复旦大学',
  '香港大学',
  '南方科技大学',
  '厦门联芯',
  '深圳市第二人民医院',
  '深圳大学总医院',
  '深圳恒生医院',
  '中远集团',
  '兴业银行',
  '聚合臻品',
  '老无忧',
  '照相机罗盘',
  '华为'
]
const customerLogos = Object.entries(customerLogoModules)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, image], index) => ({ name: customerNames[index], image }))
const hotProducts = [
  {
    id: 'hot-ai-tools',
    title: 'AI工具',
    desc: '智简高效，全能陪伴，化繁为简，让每一次操作都快人一步。',
    image: productAiTools,
    color: '#347cff',
    bg: '#edf5ff'
  },
  {
    id: 'hot-ai-commerce',
    title: 'AI电商',
    desc: '智能选品，精准推荐，洞见需求，让生意增长从此有迹可循。',
    image: productAiCommerce,
    color: '#8457ee',
    bg: '#f4efff'
  },
  {
    id: 'hot-ai-business-social',
    title: 'AI商务社交',
    desc: '智联人脉，精准匹配，高效破冰，让商业机会触手可及。',
    image: productAiBusinessSocial,
    color: '#08b979',
    bg: '#ecfff7'
  },
  {
    id: 'hot-ai-social',
    title: 'AI婚恋社交',
    desc: '智能测度，心灵契合，温柔连接，让每一段缘分恰到好处。',
    image: productAiSocial,
    color: '#fb535a',
    bg: '#fff0f1'
  },
  {
    id: 'hot-geo',
    title: 'GEO',
    desc: '智构内容，语义深耕，引擎挚友，让品牌传播直达流量之巅。',
    image: productGeo,
    color: '#ff5249',
    bg: '#fff1ef'
  },
  {
    id: 'hot-ai-academy',
    title: 'AI商学院',
    desc: '智育领航，实战赋能，前沿洞见，让管理者赢在数智时代。',
    image: productAiAcademy,
    color: '#0db77c',
    bg: '#edfff7'
  },
  {
    id: 'hot-ai-travel',
    title: 'AI文旅',
    desc: '智绘行程，沉浸体验，随心漫游，让每一场旅行都是独家记忆。',
    image: productAiTravel,
    color: '#8354f2',
    bg: '#f4efff'
  },
  {
    id: 'hot-ai-live',
    title: 'AI直播',
    desc: '智播实时，动态捕捉，深度交互，让每一帧流量都沉淀为价值。',
    image: productAiLive,
    color: '#347cff',
    bg: '#edf5ff'
  }
]
const advantages = [
  {
    title: '银行级安全防护',
    desc: '采用金融级加密与多因素认证，构筑立体防御体系。实时入侵检测与动态风控模型，精准识别异常行为，为数据资产提供安全保障。',
    image: advantageSecurity,
    color: '#1677ff',
    bg: '#edf5ff'
  },
  {
    title: '极速配置上线',
    desc: '标准化组件开箱即用，搭配可视化向导与自动化部署脚本，分钟级完成环境搭建。大幅缩短业务上线周期，抢占市场先机。',
    image: advantageFastLaunch,
    color: '#7c3aed',
    bg: '#f4efff'
  },
  {
    title: '7×24小时专家服务',
    desc: '资深工程师全天候待命，提供故障秒级响应与深度巡检。结合智能告警与人工研判，主动预防风险，保障业务连续性无忧。',
    image: advantageSupport,
    color: '#0bae68',
    bg: '#edfff6'
  },
  {
    title: '插件化灵活组合',
    desc: '采用模块化架构，支持按需加载与热插拔，业务场景自由拼装，无缝对接第三方生态，满足个性化定制需求，降低系统耦合度。',
    image: advantageModular,
    color: '#f59e0b',
    bg: '#fff7e8'
  },
  {
    title: '持续迭代升级',
    desc: '遵循敏捷研发流程，定期发布功能优化与安全补丁。融合用户反馈与技术趋势，平滑演进，让系统始终保持领先竞争力。',
    image: advantageUpgrade,
    color: '#09aee8',
    bg: '#eafaff'
  },
  {
    title: '弹性按需付费',
    desc: '采用计量制计费模式，资源随业务负载动态伸缩，无需预估容量，即用即销，成本精准可控，实现投入产出比最大化。',
    image: advantagePricing,
    color: '#fb4f58',
    bg: '#fff0f1'
  }
]
</script>

<style scoped lang="scss">
.portal-home {
  color: #182338;
  background: #fff;
}

.shell {
  width: min(1500px, calc(100% - 48px));
  margin: auto;
}

.core-shell {
  width: min(1400px, calc(100% - 48px));
}

.control-shell,
.hot-shell,
.solutions-shell,
.advantage-shell,
.customer-shell {
  width: min(1240px, calc(100% - 48px));
}

.control-center-section {
  background: linear-gradient(180deg, #f5f9ff 0%, #ffffff 72%);
}

.control-layout {
  display: grid;
  grid-template-columns: minmax(280px, 0.92fr) minmax(0, 1.5fr);
  gap: 22px;
  align-items: stretch;
}

.control-pitch {
  display: flex;
  flex-direction: column;
  padding: 28px 26px 24px;
  border-radius: 18px;
  background: linear-gradient(165deg, #ffffff 0%, #f3f8ff 100%);
  border: 0;
  box-shadow: 0 18px 42px rgb(28 112 203 / 8%);
}

.control-pitch-brand {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 22px;
}

.control-pitch-mark {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(145deg, #3b8dff, #0667f9);
  box-shadow: 0 12px 24px rgb(6 103 249 / 28%);
  flex-shrink: 0;
}

.control-pitch-eyebrow {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 700;
  color: #0667f9;
}

.control-pitch h3 {
  margin: 0;
  font-size: 28px;
  line-height: 1.28;
  color: #0f1b2d;
}

.control-pitch-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
  flex: 1;
}

.control-pitch-list li {
  display: grid;
  grid-template-columns: 28px 40px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  padding: 10px 8px;
  border-radius: 12px;
  transition: background 0.2s ease;
}

.control-pitch-list li:hover {
  background: rgb(6 103 249 / 4%);
}

.control-pitch-no {
  font-size: 12px;
  font-weight: 800;
  color: #8aa0bf;
}

.control-pitch-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
}

.control-pitch-list strong {
  display: block;
  font-size: 15px;
  color: #152230;
  margin-bottom: 2px;
}

.control-pitch-list p {
  margin: 0;
  font-size: 13px;
  color: #7a879c;
  line-height: 1.45;
}

.control-pitch-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
  padding-top: 18px;
}

.control-btn {
  min-height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s ease;
}

.control-btn.primary {
  border: 0;
  color: #fff;
  background: #0667f9;
  // box-shadow: 0 10px 22px rgb(6 103 249 / 22%);
}

.control-btn.primary:hover {
  transform: translateY(-1px);
  background: #0558d8;
}

.control-btn.ghost {
  border: 0;
  color: #0667f9;
  background: #edf5ff;
}

.control-btn.ghost:hover {
  background: #dcecff;
}

.control-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 0;
  background: transparent;
  color: #0667f9;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 4px;
}

.control-link:hover {
  opacity: 0.85;
}

.control-arch {
  padding: 28px 24px 24px;
  border-radius: 18px;
  background: #fefefe;
  border: 0;
  box-shadow: 0 18px 42px rgb(28 112 203 / 8%);
}

.control-arch-head {
  margin-bottom: 18px;
}

.control-arch-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #0667f9;
  font-size: 13px;
  font-weight: 800;
}

.control-arch-eyebrow::before {
  content: '';
  width: 22px;
  height: 2px;
  background: #0667f9;
}

.control-arch h3 {
  margin: 0;
  font-size: 24px;
  line-height: 1.35;
  color: #0f1b2d;
}

.control-arch-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.control-arch-card {
  position: relative;
  overflow: hidden;
  min-height: 168px;
  padding: 22px 18px 18px;
  border-radius: 14px;
  background: #fff;
  border: 0;
  box-shadow: 0 10px 28px rgb(28 112 203 / 5%);
  text-align: center;
  transition: 0.22s ease;
}

.control-arch-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 34px rgb(28 112 203 / 10%);
}

.control-arch-bar {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  background: var(--accent);
}

.control-arch-icon {
  width: 48px;
  height: 48px;
  margin: 6px auto 14px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: var(--accent);
  background: var(--soft);
}

.control-arch-card h4 {
  margin: 0 0 8px;
  font-size: 17px;
  color: #152230;
}

.control-arch-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #8b94a5;
}

.page-section {
  padding: 88px 0 104px;
}

.section-heading {
  text-align: center;
  margin-bottom: 40px;
}

.section-heading :deep(h2) {
  font-weight: bold;
  font-size: 46px;
  color: #000000;
  line-height: 64px;
  margin-bottom: 20px;
}

.section-heading :deep(p) {
  font-weight: 400;
  font-size: 20px;
  color: #888888;
  line-height: 28px;
}

.hero {
  width: 100%;
  aspect-ratio: 1920 / 640;
  overflow: hidden;
  position: relative;
}

.hero-carousel {
  width: 100%;
  height: 100%;
}

.hero-carousel--mobile {
  display: none;
}

.hero-carousel--desktop {
  display: block;
}

.hero-carousel :deep(.el-carousel__container) {
  height: 100% !important;
}

.hero-carousel :deep(.el-carousel__item) {
  height: 100%;
}

.hero-carousel :deep(.el-carousel__item img) {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  filter: contrast(1.04) saturate(1.02);
  backface-visibility: hidden;
  user-select: none;
  pointer-events: none;
}

.hero-carousel :deep(.el-carousel__indicators) {
  z-index: 3;
}

.hero-carousel :deep(.el-carousel__arrow) {
  z-index: 3;
}

.hero-hotspot {
  position: absolute;
  top: 79.5%;
  height: 9%;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  z-index: 4;
}

.hero-hotspot:focus-visible {
  outline: 3px solid #0a7cff;
  outline-offset: 3px;
}

.hero-trial {
  left: 18.75%;
  width: 10.4%;
}

.hero-contact {
  left: 30.75%;
  width: 10.4%;
}

.hero-hotspot--mobile {
  display: none;
}

.hero-carousel--mobile :deep(.el-carousel__container) {
  height: 100% !important;
}

.hero-carousel--mobile :deep(.el-carousel__indicators--outside) {
  margin-top: 0;
}

.hero-carousel--mobile :deep(.el-carousel__indicator) {
  padding: 10px 4px;
}

.hero-carousel--mobile :deep(.el-carousel__button) {
  width: 18px;
  height: 4px;
  border-radius: 999px;
  background: rgb(10 124 255 / 25%);
  opacity: 1;
}

.hero-carousel--mobile :deep(.el-carousel__indicator.is-active .el-carousel__button) {
  width: 24px;
  background: #0a7cff;
}

.hero-carousel--mobile :deep(.el-carousel__arrow) {
  width: 32px;
  height: 32px;
  background: rgb(255 255 255 / 88%);
  color: #0a7cff;
  border: 1px solid rgb(10 124 255 / 18%);
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}

.product-card {
  --product-icon-shift: 28px;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 390px;
  padding: 0 18px 28px;
  overflow: hidden;
  text-align: center;
  cursor: pointer;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 52%, var(--soft, #f8fbff) 100%);
  box-shadow: 0 14px 36px rgb(28 112 203 / 7%);
  box-sizing: border-box;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.product-card:hover,
.adv-grid article:hover {
  transform: translateY(-5px);
  box-shadow: 0 18px 42px rgb(28 112 203 / 12%);
}

.product-card:focus-visible {
  outline: 3p x solid color-mix(in srgb, var(--accent, #087af4) 55%, #fff);
  outline-offset: 3px;
}

/* 底部弱装饰：沿用各卡原素材下半区，不抢主视觉 */
.product-card-deco {
  position: absolute;
  left: 50%;
  bottom: -6%;
  width: 128%;
  max-width: none;
  height: 46%;
  object-fit: cover;
  object-position: center bottom;
  transform: translateX(-50%);
  opacity: 0.22;
  pointer-events: none;
  filter: saturate(0.85);
}

.product-icon-stack {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: calc(22px + var(--product-icon-shift));
  margin-bottom: 8px;
}

.product-icon-tile {
  position: relative;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  color: #fff;
  border-radius: 16px;
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--accent) 88%, #fff) 0%,
    var(--accent) 100%
  );
  box-shadow:
    0 14px 28px color-mix(in srgb, var(--accent) 32%, transparent),
    inset 0 1px 0 rgb(255 255 255 / 35%);
}

.product-icon-pedestal {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  margin-top: -10px;
  filter: drop-shadow(0 10px 16px rgb(15 40 80 / 10%));
}

.pedestal-top,
.pedestal-base {
  display: block;
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    rgb(255 255 255 / 92%) 0%,
    color-mix(in srgb, var(--soft) 55%, #fff) 100%
  );
  border: 1px solid rgb(255 255 255 / 80%);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 70%);
}

.pedestal-top {
  width: 78px;
  height: 14px;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--accent) 16%, #fff) 0%,
    rgb(255 255 255 / 78%) 100%
  );
}

.pedestal-base {
  width: 96px;
  height: 18px;
  margin-top: -6px;
  opacity: 0.95;
}

.product-copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 10px;
}

.product-card h3 {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: #0f1b2d;
  line-height: 1.3;
}

.product-card p {
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.65;
  color: #8b94a5;
}

.product-card button {
  margin-top: auto;
  min-width: 128px;
  height: 40px;
  padding: 0 22px;
  border: 0;
  border-radius: 20px;
  background: #ffffff;
  color: var(--accent, #087af4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  pointer-events: none;
  box-sizing: border-box;
}

.core-section,
.solutions-section {
  background: #fff;
}

.solution-grid {
  --solution-gap: 20px;
  --solution-radius: 12px;
  --solution-min-h: 280px;

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--solution-gap);
}

.solution-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: var(--solution-min-h);
  padding: 28px 24px 24px;
  overflow: hidden;
  color: #fff;
  cursor: pointer;
  border: 0;
  border-radius: var(--solution-radius);
  isolation: isolate;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgb(15 30 60 / 22%);
    outline: none;

    .solution-card-bg {
      transform: scale(1.06);
    }

    .solution-cta {
      gap: 8px;
    }
  }

  &:focus-visible {
    box-shadow:
      0 0 0 3px #fff,
      0 0 0 5px #087af4,
      0 18px 40px rgb(15 30 60 / 22%);
  }
}

.solution-card-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  transition: transform 0.45s ease;
}

.solution-card-mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgb(8 18 40 / 8%) 0%,
    rgb(8 18 40 / 28%) 42%,
    rgb(8 18 40 / 78%) 100%
  );
}

.solution-copy {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
}

.solution-copy h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
  letter-spacing: 0.01em;
}

.solution-copy p {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  line-height: 1.65;
  color: rgb(255 255 255 / 82%);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.solution-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.solution-tags span {
  padding: 3px 10px;
  font-size: 12px;
  line-height: 1.4;
  color: rgb(255 255 255 / 92%);
  background: rgb(255 255 255 / 16%);
  border: 0;
  border-radius: 999px;
  backdrop-filter: blur(4px);
}

.solution-cta {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  transition: gap 0.2s ease;
}

.more-btn {
  display: flex;
  margin: 44px auto 0;
  background: #087af4;
  color: #fff;
  border: 0;
  border-radius: 5px;
  padding: 12px 26px;
}

.stats-band {
  background: #075ce9 url('@/assets/portal/home/stats-cta-background.png') center / cover no-repeat;
  padding: 58px 0;
  color: #fff;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  text-align: center;
}

.stats-grid div {
  border-right: 1px solid #ffffff36;
  padding: 4px 0;
}

.stats-grid div:last-child {
  border: 0;
}

.stats-grid strong {
  font-size: 48px;
  display: block;
}

.stats-grid sup {
  font-size: 17px;
}

.stats-grid span {
  font-size: 14px;
  opacity: 0.8;
}

.logo-wall {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;
  padding: 12px 0 4px;
}

.logo-item {
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 14px rgb(30 73 122 / 5%);
  transition: 0.2s;
}

.logo-item:hover {
  box-shadow: 0 7px 18px rgb(30 73 122 / 8%);
  transform: translateY(-2px);
}

.logo-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hot-section {
  background: #f9fafc;
}

.hot-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.hot-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  padding: 18px 18px 24px;
  overflow: hidden;
  border: 0;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 28px rgb(15 27 45 / 6%);
  box-sizing: border-box;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.hot-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 36px rgb(15 27 45 / 10%);
}

.hot-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 260px;
  aspect-ratio: 564 / 320;
  margin: 0 auto 8px;
  flex-shrink: 0;
}

.hot-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  pointer-events: none;
  user-select: none;
  transform: scale(1.12);
  transform-origin: center center;
}

.hot-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
}

.hot-card h3 {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: #000;
}

.hot-card p {
  margin: 0 0 20px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;
  color: #999;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.hot-actions {
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 260px;
  margin-top: auto;
}

.hot-btn {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 20px;
  background: #f5f6f8;
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: none;
  transition:
    transform 0.2s ease,
    background 0.2s ease,
    color 0.2s ease;
}

.hot-btn:hover {
  color: #333;
  background: #eef0f3;
  transform: translateY(-1px);
}

.hot-btn:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--accent, #347cff) 45%, #fff);
  outline-offset: 2px;
}

.adv-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}

.adv-grid article {
  border: 0;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  gap: 16px;
  min-height: 0;
  box-sizing: border-box;
  background: #f7f8fa;
  box-shadow: none;
}

.adv-grid article > img.adv-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: contain;
  flex: none;
  display: block;
  background: transparent;
}

.adv-grid h3 {
  font-size: 18px;
  font-weight: bold;
  color: #000000;
  margin: 2px 0 10px;
  line-height: 1.3;
}

.adv-grid article section {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.adv-grid p {
  font-weight: 400;
  font-size: 14px;
  color: #999999;
  line-height: 1.7;
  margin: 0;
}

.adv-grid button {
  align-self: flex-start;
  width: 116px;
  height: 34px;
  margin-top: 18px;
  border: 1px solid #f5f5f5;
  border-radius: 8px;
  background: #ffffff;
  color: #333333;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: 0.2s;
}

.adv-grid button:hover {
  background: #ffffff;
  color: #333333;
  border-color: #ebebeb;
}

.cta-section {
  height: 600px;
  background: #064ee4 url('@/assets/portal/home/cta-background.png') center / cover no-repeat;
  position: relative;
  overflow: hidden;
  color: #fff;
}

.cta-inner {
  text-align: center;
  position: relative;
  padding-top: 112px;
}

.cta-eyebrow {
  display: inline-flex;
  gap: 7px;
  align-items: center;
  padding: 9px 18px;
  background: #ffffff1c;
  border-radius: 18px;
  font-size: 15px;
}

.cta-inner h2 {
  font-size: 50px;
  margin: 28px 0 14px;
}

.cta-inner > p {
  opacity: 0.78;
  margin-bottom: 36px;
  font-size: 17px;
}

.cta-inner > div:nth-of-type(2) {
  display: flex;
  justify-content: center;
  gap: 14px;
}

.cta-light,
.cta-line {
  height: 52px;
  padding: 0 34px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 18px;
}

.cta-light {
  border: 0;
  color: #0a67ef;
  background: #fff;
}

.cta-line {
  border: 1px solid #ffffff9c;
  color: #fff;
  background: transparent;
}

.cta-line img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.cta-inner aside {
  display: flex;
  justify-content: center;
  gap: 100px;
  margin-top: 44px;
  font-size: 17px;
  opacity: 1;
}

.cta-inner aside span {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* iPad / 平板：保持左右双栏，仅压缩间距与字号 */
@media (min-width: 768px) and (max-width: 1199px) {
  .control-layout {
    grid-template-columns: minmax(240px, 0.88fr) minmax(0, 1.12fr);
    gap: 16px;
  }

  .control-pitch {
    padding: 22px 18px 20px;
  }

  .control-pitch h3 {
    font-size: 22px;
  }

  .control-pitch-list strong {
    font-size: 14px;
  }

  .control-pitch-list p {
    font-size: 12px;
  }

  .control-pitch-actions {
    gap: 8px;
  }

  .control-btn {
    min-height: 36px;
    padding: 0 14px;
    font-size: 13px;
  }

  .control-link {
    font-size: 13px;
  }

  .control-arch {
    padding: 22px 16px 18px;
  }

  .control-arch h3 {
    font-size: 18px;
  }

  .control-arch-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .control-arch-card {
    min-height: 140px;
    padding: 14px 10px 12px;
  }

  .control-arch-icon {
    width: 40px;
    height: 40px;
    margin: 4px auto 10px;
  }

  .control-arch-card h4 {
    font-size: 14px;
  }

  .control-arch-card p {
    font-size: 11px;
    line-height: 1.45;
  }
}

@media (max-width: 1100px) {
  .product-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
  }

  .product-card {
    padding: 0 18px 28px;
    --product-icon-shift: 22px;
  }

  .product-card h3 {
    font-size: 22px;
  }

  .product-card p {
    font-size: 14px;
  }
}

@media (max-width: 767px) {
  .control-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .hero {
    aspect-ratio: 750 / 400;
    height: auto;
    min-height: 0;
  }

  .hero-carousel--desktop,
  .hero-hotspot:not(.hero-hotspot--mobile) {
    display: none !important;
  }

  .hero-carousel--mobile {
    display: block;
  }

  .hero-hotspot--mobile {
    display: block;
  }

  .hero-trial-mobile {
    top: 58%;
    left: 4%;
    width: 52%;
    height: 9%;
  }

  .hero-contact-mobile {
    top: 69%;
    left: 4%;
    width: 52%;
    height: 9%;
  }

  .control-arch-grid,
  .product-grid,
  .hot-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .solution-grid,
  .adv-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .logo-wall {
    grid-template-columns: repeat(4, 1fr);
  }

  .page-section {
    padding: 56px 0 64px;
  }

  .section-heading :deep(h2) {
    font-size: 34px;
    line-height: 48px;
  }

  .section-heading :deep(p) {
    font-size: 16px;
    line-height: 26px;
  }
}

@media (max-width: 600px) {
  .portal-home {
    overflow-x: hidden;
  }

  .shell {
    width: min(100% - 24px, 1500px);
  }

  .core-shell,
  .control-shell,
  .hot-shell,
  .solutions-shell {
    width: min(100% - 24px, 1240px);
  }

  .page-section {
    padding: 40px 0 48px;
  }

  .section-heading {
    margin-bottom: 24px;
  }

  .section-heading :deep(h2) {
    margin-bottom: 10px;
    font-size: 26px;
    line-height: 36px;
  }

  .section-heading :deep(p) {
    font-size: 14px;
    line-height: 22px;
  }

  .hero-trial-mobile,
  .hero-contact-mobile {
    left: 5%;
    width: 54%;
  }

  .control-arch-grid,
  .product-grid,
  .solution-grid,
  .hot-grid,
  .adv-grid {
    grid-template-columns: 1fr;
  }

  .control-pitch {
    padding: 22px 18px 20px;
  }

  .control-arch {
    padding: 22px 16px 18px;
  }

  .control-pitch h3 {
    font-size: 22px;
    line-height: 1.35;
  }

  .control-arch h3 {
    font-size: 18px;
    line-height: 1.4;
  }

  .control-pitch-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .control-btn {
    width: 100%;
  }

  .control-link {
    justify-content: center;
  }

  .product-card {
    height: auto;
    min-height: 320px;
    padding: 0 20px 24px;
    --product-icon-shift: 18px;
  }

  .product-icon-tile {
    width: 56px;
    height: 56px;
    border-radius: 14px;
  }

  .pedestal-top {
    width: 70px;
  }

  .pedestal-base {
    width: 86px;
  }

  .solution-card {
    --solution-min-h: 220px;

    min-height: var(--solution-min-h);
    padding: 24px 20px 20px;
  }

  .solution-copy h3 {
    font-size: 20px;
  }

  .solution-copy p {
    -webkit-line-clamp: 3;
  }

  .hot-card {
    padding: 16px 14px 20px;
  }

  .hot-icon-wrap {
    max-width: none;
    margin-bottom: 6px;
  }

  .hot-icon {
    transform: scale(1.08);
  }

  .hot-actions {
    max-width: none;
  }

  .hot-card p {
    min-height: 0;
    padding: 0 2px;
  }

  .adv-grid article {
    min-height: 0;
    padding: 20px;
  }

  .stats-band {
    padding: 36px 0;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 30px;
  }

  .stats-grid div:nth-child(2) {
    border-right: 0;
  }

  .stats-grid strong {
    font-size: 28px;
  }

  .logo-wall {
    grid-template-columns: repeat(2, 1fr);
  }

  .cta-inner h2 {
    font-size: 26px;
    line-height: 36px;
  }

  .cta-inner {
    padding: 48px 16px 0;
  }

  .cta-inner > div:nth-of-type(2) {
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .cta-light,
  .cta-line {
    height: 44px;
    padding: 0 18px;
    font-size: 15px;
  }

  .cta-inner aside {
    gap: 14px;
    flex-direction: column;
    align-items: center;
    margin-top: 28px;
    font-size: 14px;
  }

  .cta-section {
    height: auto;
    min-height: 380px;
    padding-bottom: 40px;
  }
}

@media (max-width: 400px) {
  .hero {
    aspect-ratio: 3 / 4;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid div {
    border-right: 0 !important;
  }

  .cta-light,
  .cta-line {
    width: 100%;
    justify-content: center;
  }
}
</style>
